import React, { useState } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';

const TokenEntry = ({ onTokenSubmit, loading }) => {
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!token.trim()) {
      setError('Please enter your access token');
      return;
    }

    if (token.length !== 8) {
      setError('Token must be 8 characters long');
      return;
    }

    onTokenSubmit(token.toUpperCase());
  };

  const handleTokenChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 8) {
      setToken(value);
      setError('');
    }
  };

  return (
    <div className="max-w-md mx-auto mt-20">
      <div className="bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-secondary-200/50 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 p-8 text-center">
          <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30">
            <KeyIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-white mb-2">
            Access Your Assessment
          </h1>
          <p className="text-primary-100">
            Enter the 8-character access token that was emailed to you
          </p>
        </div>

        {/* Form section */}
        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-secondary-700 mb-2">
                Access Token
              </label>
              <input
                type="text"
                id="token"
                value={token}
                onChange={handleTokenChange}
                placeholder="ABCD1234"
                className={`w-full px-4 py-3 bg-secondary-50 border-2 rounded-xl text-center text-2xl font-mono tracking-wider transition-all duration-300 ${
                  error
                    ? 'border-error focus:border-error focus:ring-error'
                    : 'border-secondary-200 focus:border-primary-500 focus:ring-primary-500'
                } focus:outline-none focus:ring-2 focus:ring-offset-0`}
                autoComplete="off"
                spellCheck="false"
                maxLength="8"
                disabled={loading}
              />
              {error && (
                <p className="mt-2 text-sm text-error flex items-center">
                  <span className="inline-block w-1 h-1 bg-error rounded-full mr-2"></span>
                  {error}
                </p>
              )}

              {/* Token format hint */}
              <div className="mt-3 text-xs text-secondary-500 text-center">
                Token format: 8 alphanumeric characters (e.g., ABCD1234)
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || token.length !== 8}
              className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-300 transform ${
                loading || token.length !== 8
                  ? 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-secondary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Validating...
                </span>
              ) : (
                'Access Assessment'
              )}
            </button>
          </form>

          {/* Additional help text */}
          <div className="mt-8 p-4 bg-primary-50 rounded-xl border border-primary-200/50">
            <p className="text-sm text-primary-700 text-center">
              <span className="font-medium">Need help?</span> Check your email for the access token or contact your family wealth coach.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenEntry;
